<template>
  <v-container>
    <v-layout>
      <v-flex xs12 sm8 offset-sm2>
        <div class="dietary-page iabf-dancer-theme">
          <router-link to="/" class="back-link">&larr; Back to Dashboard</router-link>

          <h1>How do you like to eat?</h1>
          <p class="subtitle">
            A few quick questions so festival meals feel good for you.
          </p>

          <div class="dietary-photos" aria-hidden="false">
            <img
              class="dietary-photo dietary-photo--wide"
              src="/static/dietary-meal-dairy-free.jpg"
              alt="Dairy-free egg dish labeled at the festival meal line"
              width="1400"
              height="933"
              loading="eager"
            />
            <img
              class="dietary-photo dietary-photo--tall"
              src="/static/dietary-meal-buffet.jpg"
              alt="Festival meal buffet in the dining hall"
              width="800"
              height="1200"
              loading="eager"
            />
          </div>

          <div v-if="dietaryClosed && !dietaryRestrictionsEntered" class="iabf-banner deadline-notice">
            <strong>We’ve locked in meal plans for the kitchen.</strong>
            If something important changes — especially an allergy — email
            <a href="mailto:hello@iabf.dance">hello@iabf.dance</a> and we’ll do our best.
          </div>

          <span v-show="!dietaryClosed && dietaryRestrictionsEntered !== true">
            <form @submit.prevent="submitDietaryRestrictions">
              <dietary-form-fields v-model="dietaryForm" subject="self" />

              <!-- Onsite coffee maker -->
              <section v-if="showCoffeeMakerSection" class="form-section coffee-section">
                <p v-if="isAdmin && !isOnsite" class="admin-preview-badge">Admin preview — normally onsite only</p>
                <h2 class="section-title">Your in-room coffee maker</h2>
                <p class="section-copy">
                  What would you like us to stock it with?
                </p>
                <coffee-maker-fields
                  v-model="coffeeMakerStock"
                  hint="Pick what you want in each row — or None if you’re set."
                />
              </section>

              <p v-if="dietError" class="form-error" role="alert">{{ dietError }}</p>
              <p v-if="dietSuccess" class="form-success" role="status">{{ dietSuccess }}</p>
              <button
                type="submit"
                class="iabf-btn iabf-btn--primary"
                :disabled="dietSaving"
              >
                {{ dietSaving ? 'Saving…' : 'Save' }}
              </button>
            </form>
          </span>

          <span v-show="dietaryRestrictionsEntered === true">
            <ul class="summary-list">
              <li>{{ dietaryDisplay() }}</li>
              <li v-if="dietDetailOptions.length">
                Happy with:
                {{ dietDetails.length ? dietDetails.join(', ') : 'None of the ones we asked about' }}
              </li>
              <li>Gluten: {{ glutenLabel }}</li>
              <li>
                We’ll watch for:
                {{ foodAllergies.length ? foodAllergies.join(', ') : 'Nothing special' }}
              </li>
              <li v-if="dietaryNotes">Notes: {{ dietaryNotes }}</li>
              <li v-if="showCoffeeMakerSection">
                Coffee station:
                {{ coffeeStationSummary }}
              </li>
              <li>Notes: {{ dietaryNotes || 'None' }}</li>
            </ul>
            <button
              v-if="!dietaryClosed"
              type="button"
              class="iabf-btn iabf-btn--primary"
              @click="reenterDiet"
            >
              Edit answers
            </button>
            <p v-else class="deadline-inline">
              Meal plans are locked for the kitchen. Email
              <a href="mailto:hello@iabf.dance">hello@iabf.dance</a> if something needs to change.
            </p>
          </span>
        </div>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import DietaryFormFields from '@/components/iabf/DietaryFormFields.vue'
import CoffeeMakerFields from '@/components/iabf/CoffeeMakerFields.vue'
import {
  DETAIL_BY_DIET,
  DIETARY_OPTIONS,
  LEGACY_GLUTEN_ANY,
  LEGACY_GLUTEN_CELIAC,
  LEGACY_GLUTEN_PREFER,
  dietDetailOptionsFor,
  dietaryPayloadFromForm,
  emptyDietaryFormState,
  glutenLabel as glutenLabelFor,
  orderedCoffeeMakerStock,
  validateDietaryForm
} from '@/config/iabf-dietary'
import { REGISTRATION_YEAR, isDietaryEntryClosed } from '@/config/iabf-registration-2027'
import { saveErrorMessage } from '@/utils/save-error'

const ONSITE_ROOMS = [
  'Standard Dorm',
  'ADA Dorm',
  'Cottage',
  'Staying with another dancer'
]

export default {
  name: 'IABFDietary',
  components: {
    DietaryFormFields,
    CoffeeMakerFields
  },
  data () {
    return {
      dietaryRestrictionsEntered: false,
      coffee: '',
      coffeeMakerStock: [],
      dietaryForm: emptyDietaryFormState(),
      dietError: '',
      dietSuccess: '',
      dietSaving: false
    }
  },
  computed: {
    user () {
      return this.$store.getters.user
    },
    currentIABFDancer () {
      return this.$store.getters.currentIABFDancer
    },
    dancerId () {
      return this.currentIABFDancer ? this.currentIABFDancer.id : null
    },
    room () {
      return (this.currentIABFDancer && this.currentIABFDancer.room) || ''
    },
    isOnsite () {
      return ONSITE_ROOMS.includes(this.room)
    },
    isAdmin () {
      return !!this.$store.getters.isAdmin
    },
    showCoffeeMakerSection () {
      return this.isOnsite || this.isAdmin
    },
    dietaryClosed () {
      return isDietaryEntryClosed(REGISTRATION_YEAR)
    },
    diet () {
      return this.dietaryForm.diet
    },
    dietDetails () {
      return this.dietaryForm.dietDetails || []
    },
    dietDetailAnswers () {
      return this.dietaryForm.dietDetailAnswers || {}
    },
    gluten () {
      return this.dietaryForm.gluten
    },
    foodAllergies () {
      return this.dietaryForm.foodAllergies || []
    },
    dietaryNotes () {
      return this.dietaryForm.dietaryNotes || ''
    },
    dietDetailOptions () {
      return dietDetailOptionsFor(this.diet)
    },
    glutenLabel () {
      return glutenLabelFor(this.gluten)
    },
    coffeeStationSummary () {
      if (this.coffeeMakerStock.length) {
        return orderedCoffeeMakerStock(this.coffeeMakerStock).join(', ')
      }
      return this.coffee || 'Nothing picked yet'
    }
  },
  created () {
    this.$store.commit('setLayout', 'iabf-layout')
    this.initDancer()
  },
  watch: {
    user (val) {
      if (val) this.initDancer()
    },
    currentIABFDancer (val) {
      if (val && val.id) this.populateFromDancer()
    }
  },
  methods: {
    initDancer () {
      if (this.dancerId) {
        this.populateFromDancer()
        return
      }
      if (!this.user) return
      const uid = this.user.id
      const email = this.user.email
      this.$store.dispatch('loadIABFDancerByUid', uid).then(() => {
        if (this.currentIABFDancer && this.currentIABFDancer.id) return
        if (email) {
          this.$store.dispatch('linkIABFDancerByEmail', { email, uid })
        }
      })
    },
    populateFromDancer () {
      const d = this.currentIABFDancer
      if (!d) return
      this.dietaryRestrictionsEntered = d.dietaryRestrictionsEntered || false
      const rawAllergies = Array.isArray(d.foodAllergies) ? d.foodAllergies : []
      const foodAllergies = rawAllergies.filter(a => !LEGACY_GLUTEN_ANY.includes(a))
      const gluten = this.resolveGlutenFromDancer(d, rawAllergies)
      const diet = d.diet || ''
      const options = DETAIL_BY_DIET[diet] || []
      let dietDetailAnswers = {}
      let dietDetails = []
      if (options.length && d.dietaryRestrictionsEntered) {
        const saved = Array.isArray(d.dietDetails) ? d.dietDetails : options
        options.forEach((opt) => {
          dietDetailAnswers[opt] = saved.includes(opt)
        })
        dietDetails = options.filter(opt => dietDetailAnswers[opt])
      }
      this.dietaryForm = {
        diet,
        dietDetails,
        dietDetailAnswers,
        gluten,
        foodAllergies,
        dietaryNotes: d.dietaryNotes || ''
      }
      if (Array.isArray(d.coffeeMakerStock) && d.coffeeMakerStock.length) {
        this.coffeeMakerStock = [...d.coffeeMakerStock]
      } else if (typeof d.coffee === 'string' && d.coffee.trim()) {
        this.coffee = d.coffee
        this.coffeeMakerStock = []
      } else {
        this.coffee = ''
        this.coffeeMakerStock = []
      }
    },
    reenterDiet () {
      this.dietaryRestrictionsEntered = false
      this.dietError = ''
      this.dietSuccess = ''
    },
    dietaryDisplay () {
      if (!this.dietaryRestrictionsEntered) {
        return 'We don’t have your meal notes yet'
      }
      const match = DIETARY_OPTIONS.find(option => option.key === this.diet)
      if (match) {
        return match.short + ' — ' + match.description
      }
      return this.diet || 'Still getting to know how you eat'
    },
    resolveGlutenFromDancer (d, rawAllergies) {
      if (d.gluten === 'ok' || d.gluten === 'prefer-none' || d.gluten === 'celiac') {
        return d.gluten
      }
      const allergies = Array.isArray(rawAllergies) ? rawAllergies : []
      const avoided = Array.isArray(d.foodsNotConsumed) ? d.foodsNotConsumed : []
      const combined = allergies.concat(avoided)
      if (combined.some(x => LEGACY_GLUTEN_CELIAC.includes(x))) {
        return 'celiac'
      }
      if (combined.some(x => LEGACY_GLUTEN_PREFER.includes(x))) {
        return 'prefer-none'
      }
      if (d.dietaryRestrictionsEntered) {
        return 'ok'
      }
      return ''
    },
    submitDietaryRestrictions () {
      this.dietError = ''
      this.dietSuccess = ''
      const err = validateDietaryForm(this.dietaryForm)
      if (err) {
        // Dancer-facing wording for self form
        this.dietError = err
          .replace('they usually', 'you usually')
          .replace('for them', 'for you')
          .replace('they’re good', 'you’re good')
          .replace('we should watch for', 'we should watch for')
        if (err.includes('usually eat')) {
          this.dietError = 'Pick how you usually eat so we know where to start.'
        } else if (err.includes('gluten')) {
          this.dietError = 'Tell us about gluten — yes, skip it, or very sensitive.'
        } else if (err.includes('allergies')) {
          this.dietError = 'Tap any allergies we should watch for — or None if you’re good.'
        }
        return
      }
      if (!this.dancerId) {
        this.dietError = 'Hang tight — your registration is still loading. Try again in a moment.'
        return
      }
      const stock = this.showCoffeeMakerSection
        ? orderedCoffeeMakerStock(this.coffeeMakerStock)
        : []
      const coffeeLabel = stock.length
        ? stock.join(', ')
        : (this.showCoffeeMakerSection ? '' : (this.coffee || ''))

      const dietary = dietaryPayloadFromForm(this.dietaryForm)
      this.dietSaving = true
      this.$store.dispatch('updateDietaryRestrictions', {
        id: this.dancerId,
        diet: dietary.diet,
        dietDetails: dietary.dietDetails,
        gluten: dietary.gluten,
        foodsNotConsumed: dietary.foodsNotConsumed,
        foodAllergies: dietary.foodAllergies,
        coffee: coffeeLabel,
        coffeeMakerStock: stock,
        dietaryNotes: dietary.dietaryNotes || '',
        dietaryRestrictionsEntered: true
      }).then(() => {
        this.dietaryRestrictionsEntered = true
        this.dietaryForm = Object.assign({}, this.dietaryForm, {
          dietDetails: dietary.dietDetails
        })
        this.coffee = coffeeLabel
        this.dietSuccess = 'All saved — thank you!'
        this.dietSaving = false
      }).catch((err) => {
        this.dietError = saveErrorMessage(err)
        this.dietSaving = false
      })
    }
  }
}
</script>

<style scoped>
.dietary-page {
  max-width: 560px;
  margin: 0 auto;
  padding: 40px 20px 60px;
  color: var(--iabf-ink, #1c1b1f);
}
.back-link {
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: var(--iabf-ink-faint, #8a8792);
  text-decoration: none;
}
.back-link:hover { color: var(--iabf-ink, #1c1b1f); }
h1 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 36px;
  letter-spacing: -.02em;
  margin: 16px 0 8px;
  color: var(--iabf-ink, #1c1b1f);
}
.subtitle {
  font-size: 17px;
  color: var(--iabf-ink-muted, #5c5a63);
  margin-bottom: 20px;
  line-height: 1.5;
}
.dietary-photos {
  display: grid;
  grid-template-columns: 1.35fr 1fr;
  gap: 10px;
  margin: 0 0 28px;
  align-items: stretch;
}
.dietary-photo {
  display: block;
  width: 100%;
  height: 100%;
  max-height: 260px;
  object-fit: cover;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(28, 27, 31, 0.04), 0 8px 24px rgba(28, 27, 31, 0.08);
}
.dietary-photo--tall {
  object-position: center 20%;
}
@media (max-width: 520px) {
  .dietary-photos {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
  .dietary-photo {
    max-height: 180px;
  }
}
.form-section {
  margin-bottom: 28px;
}
.form-question {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 20px;
  color: var(--iabf-ink, #1c1b1f);
  margin: 0 0 10px;
  line-height: 1.3;
}
.optional-tag {
  font-family: var(--iabf-font-body, inherit);
  font-size: 13px;
  font-style: italic;
  font-weight: 400;
  color: var(--iabf-ink-faint, #8a8792);
  margin-left: 4px;
}
.field-hint {
  font-size: 14px;
  color: var(--iabf-ink-faint, #8a8792);
  font-style: italic;
  margin: 0 0 12px;
  line-height: 1.4;
}
.diet-detail {
  margin-top: 10px;
  font-style: normal;
  color: var(--iabf-ink-muted, #5c5a63);
}
.detail-answer-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.detail-answer-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.detail-answer-label {
  font-size: 16px;
  font-weight: 500;
  color: var(--iabf-ink, #1c1b1f);
  min-width: 100px;
}
.pill-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.notes-input {
  width: 100%;
}
.coffee-section {
  margin-top: 8px;
  padding: 20px 20px 8px;
  background: #fff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(28, 27, 31, 0.04), 0 8px 24px rgba(28, 27, 31, 0.06);
}
.coffee-maker-photo {
  display: block;
  width: 100%;
  max-width: 100%;
  height: auto;
  aspect-ratio: 1;
  object-fit: cover;
  border-radius: 12px;
  margin: 0 0 14px;
}
.admin-preview-badge {
  display: inline-block;
  margin: 0 0 10px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #f0eef4;
  color: var(--iabf-secondary, #8e82a6);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.03em;
}
.section-title {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 22px;
  margin: 0 0 8px;
  color: var(--iabf-ink, #1c1b1f);
}
.section-copy {
  font-size: 16px;
  line-height: 1.5;
  color: var(--iabf-ink-muted, #5c5a63);
  margin: 0 0 12px;
}
.coffee-group {
  margin-bottom: 18px;
}
.coffee-subhead {
  font-family: var(--iabf-font-body, inherit);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--iabf-ink-faint, #8a8792);
  margin: 0 0 8px;
}
.form-error {
  color: var(--iabf-danger, #b54a4a);
  font-size: 15px;
  margin: 12px 0 8px;
}
.form-success {
  color: var(--iabf-success, #5f7a55);
  font-size: 15px;
  margin: 12px 0 8px;
}
.deadline-notice {
  background: #fff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(28, 27, 31, 0.04), 0 8px 24px rgba(28, 27, 31, 0.06);
  padding: 20px 22px;
  margin: 12px 0;
  font-size: 16px;
  line-height: 1.5;
}
.deadline-notice strong {
  display: block;
  margin-bottom: 6px;
}
.deadline-notice a, .deadline-inline a { color: var(--iabf-primary, #8a957d); }
.deadline-inline {
  font-size: 14px;
  color: var(--iabf-ink-faint, #8a8792);
  font-style: italic;
  margin-top: 8px;
}
.summary-list {
  padding-left: 20px;
  margin: 8px 0 20px;
}
.summary-list li {
  font-size: 16px;
  line-height: 1.6;
  color: var(--iabf-ink-muted, #5c5a63);
  margin-bottom: 6px;
}
</style>
